import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Row,
  Segmented,
  Space,
  Switch,
  Tag,
  Typography
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import { useTranslation } from "react-i18next";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import GeneralModal from "../General.modal";
import DropdownComp from "../../../commons/Dropdowns/DropdownComp";
import MainButtonComp from "../../../commons/Buttons/MainButtonComp";
import DeleteModal from "../../../commons/DeleteModal/DeleteModal";
import { Success } from "../../../commons/Messages/Messages";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { branchAddress } from "../../../commons/Billing/customerBranches";

const { Text } = Typography;
const headers = () => ({ Authorization: "Bearer " + localStorage.getItem("token") });
const url = (customerId, branchId = "") =>
  `${process.env.REACT_APP_BACKEND_URL}/users/clients/${customerId}/branches${branchId ? `/${branchId}` : ""}`;

const EMPTY = {
  name: "",
  code: "",
  contactName: "",
  email: "",
  phone: "",
  address: "",
  zipCode: "",
  city: "",
  country: "",
  buyerReference: "",
  leitwegId: "",
  vatId: "",
  defaultBillTo: "branch",
  notes: ""
};

/**
 * Niederlassungen / Filialen of a customer. Invoices, offers and projects can be addressed to a
 * branch, or to the head office with the branch as place of service. A branch that documents
 * use is archived instead of deleted.
 */
const CustomerBranches = ({ customer, onChange }) => {
  const { t: ta } = useTranslation("accounting");
  const [form] = Form.useForm();
  const [branches, setBranches] = useState([]);
  const [usage, setUsage] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, branch = edit
  const [showArchived, setShowArchived] = useState(false);
  const customerId = customer?._id;

  const apply = data => {
    const list = Array.isArray(data?.branches) ? data.branches : [];
    setBranches(list);
    setUsage(data?.usage && typeof data.usage === "object" ? data.usage : {});
    onChange?.(list);
  };

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      apply((await axios.get(url(customerId), { headers: headers() })).data);
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const open = branch => {
    setEditing(branch || {});
    form.setFieldsValue({ ...EMPTY, ...(branch || {}) });
  };

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const res = editing?._id
        ? await axios.put(url(customerId, editing._id), values, { headers: headers() })
        : await axios.post(url(customerId), values, { headers: headers() });
      apply(res.data);
      Success(ta("Branch saved"));
      setEditing(null);
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (branch, active) => {
    try {
      apply(
        (await axios.put(url(customerId, branch._id), { active }, { headers: headers() })).data
      );
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const remove = async branch => {
    try {
      const res = await axios.delete(url(customerId, branch._id), { headers: headers() });
      apply(res.data);
      Success(
        res.data?.archived
          ? ta("The branch is used by documents and was archived")
          : ta("Branch deleted")
      );
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const usedIn = branch => {
    const u = usage[String(branch._id)] || {};
    return Object.values(u).reduce((s, n) => s + n, 0);
  };

  const archivedCount = branches.filter(b => b.active === false).length;
  const rows = branches.filter(b => showArchived || b.active !== false);

  const columns = [
    {
      title: ta("Branch"),
      dataIndex: "name",
      key: "name",
      mobile: "title",
      render: (name, b) => (
        <Space size={4} wrap>
          <strong>{name}</strong>
          {b.code ? <Text type="secondary">#{b.code}</Text> : null}
          {b.active === false ? <Tag>{ta("Archived")}</Tag> : null}
        </Space>
      )
    },
    {
      title: ta("Address"),
      key: "address",
      mobile: "subtitle",
      render: b => branchAddress(b) || <Text type="warning">{ta("No address")}</Text>
    },
    {
      title: ta("Documents go to"),
      dataIndex: "defaultBillTo",
      key: "defaultBillTo",
      render: v => (v === "head_office" ? ta("The head office") : ta("The branch"))
    },
    {
      title: ta("Contact"),
      key: "contact",
      render: b => [b.contactName, b.email].filter(Boolean).join(" · ") || "–"
    },
    {
      title: ta("Reference / cost centre"),
      key: "reference",
      render: b => b.leitwegId || b.buyerReference || "–"
    },
    {
      title: ta("Documents"),
      key: "usage",
      mobile: "badge",
      render: b => usedIn(b) || "–"
    },
    {
      title: ta("Action"),
      key: "action",
      mobile: "actions",
      render: b => (
        <DropdownComp
          menuItems={[
            {
              key: "edit",
              component: () => <MainButtonComp text="Edit" editBtn onClick={() => open(b)} />
            },
            b.active === false
              ? {
                  key: "restore",
                  component: () => (
                    <Button type="link" onClick={() => setActive(b, true)}>
                      {ta("Restore")}
                    </Button>
                  )
                }
              : {
                  key: "delete",
                  component: () => <DeleteModal text={ta("Branch")} onClick={() => remove(b)} />
                }
          ]}
        />
      )
    }
  ];

  const field = (name, label, props = {}, rules) => (
    <Form.Item name={name} label={label} rules={rules}>
      <Input {...props} />
    </Form.Item>
  );

  return (
    <div className="customer-branches">
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 12 }} wrap>
        <Text type="secondary">
          {ta(
            "Invoices, offers and projects can be addressed to a branch. Payment terms, customer number and open items stay with the customer."
          )}
        </Text>
        <Space wrap>
          {archivedCount ? (
            <Space size={6}>
              <Switch size="small" checked={showArchived} onChange={setShowArchived} />
              <Text>
                {ta("Show archived")} ({archivedCount})
              </Text>
            </Space>
          ) : null}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => open(null)}>
            {ta("Add branch")}
          </Button>
        </Space>
      </Space>
      <ResponsiveTable
        rowKey="_id"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={false}
        locale={{ emptyText: ta("No branches yet") }}
      />
      <GeneralModal
        open={Boolean(editing)}
        title={editing?._id ? ta("Edit branch") : ta("Add branch")}
        okText={ta("Save")}
        confirmLoading={saving}
        onOk={save}
        onCancel={() => setEditing(null)}
        items={
          <Form form={form} layout="vertical">
            <Row gutter={[16, 0]}>
              <Col xs={24} md={16}>
                {field("name", ta("Name"), { placeholder: ta("e.g. Branch Cologne") }, [
                  { required: true, message: ta("The branch needs a name") }
                ])}
              </Col>
              <Col xs={24} md={8}>
                {field("code", ta("Branch number"))}
              </Col>
              <Col xs={24}>{field("address", ta("Street and number"))}</Col>
              <Col xs={8} md={6}>
                {field("zipCode", ta("ZIP"), { inputMode: "numeric" })}
              </Col>
              <Col xs={16} md={10}>
                {field("city", ta("City"))}
              </Col>
              <Col xs={24} md={8}>
                {field("country", ta("Country"), {
                  placeholder: customer?.country || "Deutschland"
                })}
              </Col>
              <Col xs={24} md={12}>
                {field("contactName", ta("Contact person"))}
              </Col>
              <Col xs={24} md={12}>
                {field("phone", ta("Phone"), { inputMode: "tel" })}
              </Col>
              <Col xs={24}>
                {field(
                  "email",
                  ta("Email for invoices and offers"),
                  { type: "email", placeholder: customer?.email || "" },
                  [{ type: "email", message: ta("Please enter a valid email address") }]
                )}
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="defaultBillTo"
                  label={ta("Address documents for this branch to")}
                  extra={ta(
                    "Head office: the invoice goes to the customer's main address and names the branch as place of service."
                  )}
                >
                  <Segmented
                    block
                    options={[
                      { value: "branch", label: ta("The branch") },
                      { value: "head_office", label: ta("The head office") }
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                {field("buyerReference", ta("Reference / cost centre"))}
              </Col>
              <Col xs={24} md={12}>
                {customer?.isPublicClient
                  ? field("leitwegId", ta("Leitweg-ID of this branch"), {
                      placeholder: customer?.leitwegId || ""
                    })
                  : field("vatId", ta("VAT ID (only for a branch abroad)"))}
              </Col>
              <Col xs={24}>
                <Form.Item name="notes" label={ta("Notes")}>
                  <Input.TextArea rows={2} maxLength={2000} />
                </Form.Item>
              </Col>
            </Row>
            <Alert
              type="info"
              showIcon
              message={ta(
                "A branch is part of the same company. If the branch is its own legal entity, create it as a separate customer."
              )}
            />
          </Form>
        }
      />
    </div>
  );
};

export default CustomerBranches;
